import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PageWrapper } from '@components/PageWrapper';
import { useOfferEvent } from '@hooks/useOfferEvent';
import { addMetadataToUrl, getAllMetadataParams, useQueryParams } from '@hooks/useQueryParams';
import { useCheckoutActions, useCheckoutState } from '@services/checkoutClient';
import GoogleTagManager from '@services/GoogleTagManager';
import { useSharedDataActions, useSharedDataState } from '@store/sharedData';

import { AddOnsForm } from '@xceedsrl/adyenjs';

const MULTIPLE_ADDON_CHOICE = 'multiple_choice';
export const AddOns = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { channel, offerId } = useParams();
  const ref = useRef();
  const query = useQueryParams();
  const quantity = query.get('quantity');
  const addOnsQuery = query.get('addons');
  const metadataQuery = useMemo(() => getAllMetadataParams(query), [query.toString()]);

  const redirectUrl = query.get('redirectUrl');

  const { changeQty, addProduct, addEventInfo, addRedirectUrl } = useCheckoutActions();
  const { setAddOns, setMetadata } = useSharedDataActions();
  const { addOns, metadata } = useSharedDataState();
  const { configuration, shoppingCart } = useCheckoutState();

  const { event, offer } = useOfferEvent({ offerId, channel, lang: i18n.language });
  const normalizeAddOnsQuantity = addOnsArray =>
    (addOnsArray ?? []).map(a => ({ ...a, quantity: Number(quantity) }));
  const translations = {
    personText: t('eventPage:addOnsPersonText'),
    bookingText: t('eventPage:addOnsBookingText'),
    headerText: t('eventPage:addOnsHeaderText'),
    headerTextSingle: t('eventPage:addOnsHeaderTextSingle'),
    optional: t('eventPage:addOnsHeaderOptional'),
    bottomText:
      addOns && addOns.length > 0
        ? ''
        : t('eventPage:addOnsBottomText', { buttonText: t('common:checkout') }),
  };

  useEffect(() => {
    if (redirectUrl) {
      addRedirectUrl(redirectUrl);
    }
  }, []);

  useEffect(() => {
    if (metadataQuery !== metadata) {
      setMetadata(metadataQuery);
    }
  }, [metadataQuery, metadata]);

  // On reload, if addons are present in the query, select them if they match the offer
  useEffect(() => {
    if (!offer || !addOnsQuery) return;
    const parsed = (() => {
      try {
        return JSON.parse(addOnsQuery);
      } catch (error) {
        return [];
      }
    })();
    if (!Array.isArray(parsed)) return;
    const validIds = new Set((offer.addOns ?? []).map(a => a.id));
    const matched = parsed.reduce((acc, a) => {
      if (a && validIds.has(a.id)) acc.push({ ...a, quantity: Number(quantity) });
      return acc;
    }, []);
    setAddOns(matched);
  }, [offer, addOnsQuery]);

  useEffect(() => {
    if (offer) {
      addProduct(offer, addOns);
      changeQty(quantity, offer.id, addOns);
    }
  }, [offer]);

  useEffect(() => {
    if (event) {
      addEventInfo(event);
    }
  }, [event]);

  // If there are no add-ons (or none available for the requested quantity), skip this step
  useEffect(() => {
    if (!offer || !event) return;
    const qty = Number(quantity) || 0;
    const hasAvailableAddOns =
      (offer?.addOns?.length ?? 0) > 0 &&
      offer.addOns.some(addOn => addOn.availableItems === null || addOn.availableItems >= qty);

    if (!hasAvailableAddOns) {
      const params = new URLSearchParams(location.search);
      // ensure redirectUrl is present if configured
      if (configuration.redirectUrl && !params.has('redirectUrl')) {
        params.set('redirectUrl', configuration.redirectUrl);
      }
      // remove existing metadata to avoid duplication
      Array.from(params.keys()).forEach(key => {
        if (key.startsWith('metadata')) {
          params.delete(key);
        }
      });
      // do not carry addons when skipping (remove if present)
      params.delete('addons');
      const baseUrl = `/${channel}/offer/${offer.id}/form?${params.toString()}`;
      const finalUrl = addMetadataToUrl(baseUrl, metadata);
      history.push(finalUrl);
    }
  }, [offer, event, quantity]);

  if (!offer || !event) return null;

  // Safe to derive values once offer/event are available
  const isBottleService = offer.admissionType === 'bottleservice';
  const isMultipleChoice = offer.addOnChoiceMode === MULTIPLE_ADDON_CHOICE;
  const isAddonRequired = offer.hasMandatoryAddOns;
  const buttonText = t('common:checkout');
  const handleSubmitOutside = () => {
    if (typeof window?.dataLayer?.push === 'function')
      GoogleTagManager.addOns({
        offer,
        event,
        shoppingCart,
        channel,
        quantity,
        addOnsSelected: addOns,
      });
    const params = new URLSearchParams(location.search);
    // ensure redirectUrl is present if configured
    if (configuration.redirectUrl && !params.has('redirectUrl')) {
      params.set('redirectUrl', configuration.redirectUrl);
    }
    // remove existing metadata to avoid duplication
    Array.from(params.keys()).forEach(key => {
      if (key.startsWith('metadata')) {
        params.delete(key);
      }
    });
    // set addons to the current selection (normalized to current ticket quantity)
    params.delete('addons');
    params.set('addons', JSON.stringify(normalizeAddOnsQuantity(addOns)));
    const baseUrl = `/${channel}/offer/${offer.id}/form?${params.toString()}`;
    const finalUrl = addMetadataToUrl(baseUrl, metadata);
    history.push(finalUrl);
  };

  return (
    <PageWrapper
      isDisabled={false}
      buttonText={buttonText}
      isFormValid={(isAddonRequired && (addOns?.length ?? 0) > 0) || !isAddonRequired}
      handleSubmit={handleSubmitOutside}
    >
      <AddOnsForm
        addOns={offer.addOns}
        i18n={translations}
        lang={i18n.language}
        ticketQuantity={quantity}
        ref={ref}
        addOnsSelected={addOns ?? []}
        setAddOnsSelected={selected => setAddOns(normalizeAddOnsQuantity(selected))}
        isBottleService={isBottleService}
        isRequired={isAddonRequired}
        isMultipleChoice={isMultipleChoice}
      />
    </PageWrapper>
  );
};
